import React, { useEffect, useState } from "react";
import styles from "./home.module.css";
import { Col, Row, Table, Form, InputGroup, Button, Badge, Card, Modal } from "react-bootstrap";
import Page from "../../Component/Pagination/Page";
import Header from "../../Component/Header/Header";
import { Helmet } from "react-helmet";
import Notification from "../../Component/Notification/Notification";
import { useAuth } from "../../Context/AuthContext";
import { EDIT_APPLICATION, GET_APPLICATIONS } from "../../APIs/BaseUrl";
import Moment from "react-moment";
import { useLocation } from "react-router-dom";

const statusVariant = (status) => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "success";
    case "pending":
      return "warning";
    case "rejected":
      return "danger";
    default:
      return "secondary";
  }
};

const Applications = () => {
  const { toggleNotification } = useAuth();

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQuery();
  const researchId = query.get("researchId");

  const [applications, setApplications] = useState([]);
  // eslint-disable-next-line
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [filteredApps, setFilteredApps] = useState([]);

  const [pagination, setPagination] = useState({
    totalDocs: 0,
    limit: limit,
    totalPages: 1,
    page: 1,
  });

  const validStatuses = ["applied", "review", "shortlist", "complete", "reject"];

  const handleStatusChange = async (appId, newStatus) => {
    if (!validStatuses.includes(newStatus)) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${EDIT_APPLICATION(appId)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) throw new Error("Failed to update status");
      setApplications((prev) =>
        prev.map((app) =>
          app.id === appId ? { ...app, status: newStatus } : app
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalAppId, setModalAppId] = useState(null);
  const [modalStatus, setModalStatus] = useState("");

  const openStatusModal = (appId, currentStatus) => {
    setModalAppId(appId);
    setModalStatus(currentStatus);
    setShowModal(true);
  };

  const closeStatusModal = () => {
    setShowModal(false);
    setModalAppId(null);
    setModalStatus("");
  };

  const updateStatus = async () => {
    await handleStatusChange(modalAppId, modalStatus);
    closeStatusModal();
  };

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${GET_APPLICATIONS}?research=${researchId}&page=${page}&limit=${limit}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch applications");
        }
        const data = await response.json();
        setApplications(data?.data?.docs || []);
        setPagination({
          totalDocs: data?.data?.totalDocs,
          limit: data?.data?.limit,
          totalPages: data?.data?.totalPages,
          page: data?.data?.page,
        });
      } catch (err) {
        setError(err.message);
      }
    };

    fetchApplications();
    // eslint-disable-next-line
  }, [page, limit]);

  useEffect(() => {
    if (!search) {
      setFilteredApps(applications);
    } else {
      setFilteredApps(
        applications.filter(
          (app) =>
            app.research_id?.research_name?.toLowerCase().includes(search.toLowerCase()) ||
            app.applicant_id?.name?.toLowerCase().includes(search.toLowerCase()) ||
            app.applicant_id?.email?.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
    // eslint-disable-next-line
  }, [search, applications]);

  return (
    <React.Fragment>
      <div className="right-aside" style={{ background: "#f8f9fa", minHeight: "100vh" }}>
        <Helmet>
          <title>Applications</title>
        </Helmet>
        <Row>
          <Col md={!toggleNotification ? 12 : 8} sm={12}>
            <Header />
            <div className={styles.head_sec}>
              <Card className="mb-4 shadow-sm border-0">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 fw-bold">Applications</h5>
                    <InputGroup style={{ maxWidth: 320 }}>
                      <Form.Control
                        type="text"
                        placeholder="Search by research or applicant"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="shadow-sm"
                      />
                      <Button variant="outline-secondary" onClick={() => setSearch("")}>
                        Clear
                      </Button>
                    </InputGroup>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <Table
                      striped
                      bordered
                      hover
                      responsive
                      size="sm"
                      className="align-middle shadow-sm bg-white"
                      style={{ borderRadius: 8, overflow: "hidden" }}
                    >
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th style={{whiteSpace : "nowrap"}}>Research</th>
                          <th style={{whiteSpace : "nowrap"}}>Applied At</th>
                          <th style={{whiteSpace : "nowrap"}}>Status</th>
                          <th style={{whiteSpace : "nowrap"}}>Applicant</th>
                          <th style={{whiteSpace : "nowrap"}}>Answers</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredApps.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center py-4 text-muted">
                              No applications found.
                            </td>
                          </tr>
                        ) : (
                          filteredApps.map((app, idx) => (
                            <tr key={app.id}>
                              <td>{(page - 1) * limit + idx + 1}</td>
                              <td style={{whiteSpace : "nowrap"}}>
                                <span className="fw-semibold">{app.research_id?.research_name || "N/A"}</span>
                              </td>
                              <td style={{whiteSpace : "nowrap"}}>
                                <Moment format="YYYY-MM-DD HH:mm:ss">{app.createdAt}</Moment>
                                <br />
                                <small className="text-muted">
                                  (<Moment fromNow>{app.createdAt}</Moment>)
                                </small>
                              </td>
                              <td style={{whiteSpace : "nowrap"}}>
                                <Badge
                                  bg={statusVariant(app.status)}
                                  className="px-3 py-2 text-capitalize"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => openStatusModal(app.id, app.status)}
                                >
                                  {app.status}
                                </Badge>
                              </td>
                              <td style={{whiteSpace : "nowrap"}}>
                                <span className="fw-semibold">{app.applicant_id?.name}</span>
                                <br />
                                <small className="text-muted">{app.applicant_id?.email}</small>
                                <br />
                                <small className="text-muted">{app.applicant_id?.phone}</small>
                              </td>
                              <td style={{whiteSpace : "nowrap"}}>
                                {app.answers && app.answers.length > 0 ? (
                                  <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
                                    {app.answers.map((ans, i) => (
                                      <li key={i} style={{ fontSize: 12 }}>
                                        {JSON.stringify(ans.answersText)}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span style={{ fontSize: 12, color: "#888" }}>No answers</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="d-flex justify-content-end mt-3">
                    <Page {...pagination} onPageChange={setPage} />
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>
          {toggleNotification && (
            <Col md={4} className="p-0">
              <Notification />
            </Col>
          )}
        </Row>

        {/* Status Modal */}
        <Modal show={showModal} onHide={closeStatusModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Update Application Status</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Select Status</Form.Label>
              <Form.Select
                value={modalStatus}
                onChange={(e) => setModalStatus(e.target.value)}
              >
                {validStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeStatusModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={updateStatus}
              disabled={!modalStatus || modalStatus === ""}
            >
              Update
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Applications;
